import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import {
  Participation,
  useIncentivePaymentForParticipation,
  usePayIncentive,
  useParticipant,
  useSession,
  useRemoveParticipationIncentivePayment,
  useMarkAsPayed,
  IncentivePaymentStatus,
  useCurrencies,
  useUpdateIncentivePayment,
  useMarkAsRequested,
} from '@app/api';
import {
  Chip,
  Button,
  SquareButton,
  useToast,
  useBoolModalState,
  SubmitButton,
  InformationBox,
} from '@app/core';
import { toCurrency, toEuros } from 'i18n';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { CurrencyCodeSelect, SubmitButtonsLine } from 'components';
import moment from 'moment';
import {
  getCurrencyAmountAsFloat,
  getFloatCurrencyAmountAsInteger,
} from 'lib/conversions';
import { CountryKeySelect, Input } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  incentivePayment: {
    ...theme.fonts.caption,
    display: 'flex',
    gap: theme.marginBase * 2,
    alignItems: 'center',
  },
  incentivePaymentCell: {},
  spacer: {
    flex: 1,
  },
  sameLineForm: {
    display: 'flex',
    gap: theme.marginBase * 2,
    alignItems: 'center',
  },
}));

const incentivePaymentStatusColor: Record<IncentivePaymentStatus, Colors> = {
  [IncentivePaymentStatus.REQUESTED]: 'blue',
  [IncentivePaymentStatus.PAYED]: 'green',
  [IncentivePaymentStatus.CANCELLED]: 'red',
  [IncentivePaymentStatus.ERROR]: 'red',
};

interface Props {
  participation: Participation;
}
export const IncentivePaymentBlock = ({ participation }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { i18n } = useTranslation();

  const { data: session } = useSession(participation.sessionId);
  const { data: incentivePayment, isLoading } =
    useIncentivePaymentForParticipation(participation.id);

  const { data: participant } = useParticipant(
    !incentivePayment && !isLoading ? participation.participantId : undefined,
  );
  const { mutateAsync: payIncentive } = usePayIncentive(participation.id);

  const [editing, startEditing, stopEditing] = useBoolModalState();

  const { mutateAsync: updateIncentivePayment } = useUpdateIncentivePayment(
    incentivePayment?.id,
  );
  const toastParams = useMemo(
    () => ({
      onSuccess: () => toast.saved(),
      onError: (e: any) => toast.exception(e),
    }),
    [toast],
  );

  const { mutate: mutateMarkAsPayed } = useMarkAsPayed(
    incentivePayment?.id,
    toastParams,
  );
  const { mutate: mutateMarkAsRequested } = useMarkAsRequested(
    incentivePayment?.id,
    toastParams,
  );

  const { data: currencies } = useCurrencies();

  const { mutate: removeIncentivePayment } =
    useRemoveParticipationIncentivePayment(participation.id, toastParams);

  const markAsRequested = useCallback(
    () => mutateMarkAsRequested(),
    [mutateMarkAsRequested],
  );
  const markAsPayed = useCallback(
    () => mutateMarkAsPayed(),
    [mutateMarkAsPayed],
  );
  const remove = useCallback(
    () => removeIncentivePayment(),
    [removeIncentivePayment],
  );

  const date = useMemo(() => {
    const date = incentivePayment?.payedAt;
    if (!date) {
      return undefined;
    }
    return Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(moment(date).toDate());
  }, [incentivePayment, i18n.language]);

  const payParticipationIncentive = useCallback(async () => {
    try {
      await payIncentive();
      toast.saved();
    } catch (e) {
      toast.exception(e);
    }
  }, [payIncentive, toast, participant, session]);

  const initialValues = useMemo(() => {
    const currency = currencies?.find(
      (c) => c.code === incentivePayment?.currency,
    );
    return {
      incentive: getCurrencyAmountAsFloat(
        incentivePayment?.incentive || 0,
        currency?.decimal,
      ),
      currency: incentivePayment?.currency || 'EUR',
      countryKey: incentivePayment?.countryKey || 'FR',
    };
  }, [incentivePayment]);

  const onSubmitEdition = useCallback(
    async (values: typeof initialValues) => {
      try {
        const selectedCurrency = values.currency || 'EUR';
        const currency = currencies?.find((c) => c.code === selectedCurrency);

        const dto = {
          incentive: getFloatCurrencyAmountAsInteger(
            values.incentive,
            currency?.decimal,
          ),
          currency: values?.currency || 'EUR',
          countryKey: values?.countryKey || 'FR',
        };

        await updateIncentivePayment(dto);
        stopEditing();
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [toast, stopEditing, updateIncentivePayment, currencies],
  );

  if (!participation.participantId) {
    return (
      <InformationBox
        variant="danger"
        text="Le participant n'a pas donnée son email. Nous ne pouvons lui envoyer de Contrepartie"
      />
    );
  }

  if (!incentivePayment) {
    return (
      <Button
        text="Envoyer la Contrepartie"
        color="blue"
        small
        onClick={payParticipationIncentive}
      />
    );
  }

  return (
    <>
      <div className={classes.incentivePayment}>
        <div className={classes.incentivePaymentCell}>
          <Chip color={incentivePaymentStatusColor[incentivePayment.status]}>
            {incentivePayment.status}
          </Chip>
        </div>
        <div className={classes.incentivePaymentCell}>
          {incentivePayment.incentive !== undefined
            ? toCurrency(
                incentivePayment.incentive,
                false,
                incentivePayment.currency,
              )
            : '(Vide)'}{' '}
          ({incentivePayment.currency || 'EUR'})
        </div>
        <div className={classes.incentivePaymentCell}>
          {incentivePayment.countryKey || 'Pas de pays'}
        </div>
        {incentivePayment.eurAmount && (
          <div className={classes.incentivePaymentCell}>
            ({toEuros(incentivePayment.eurAmount)})
          </div>
        )}
        {incentivePayment.payedAt && (
          <div className={classes.incentivePaymentCell}>
            Payé le {date} par {incentivePayment.payedBy}
          </div>
        )}
        <div className={classes.spacer} />
        {incentivePayment.status === IncentivePaymentStatus.REQUESTED && (
          <>
            <div className={classes.incentivePaymentCell}>
              <SquareButton
                icon="ri-edit-line"
                color="blue"
                small
                onClick={startEditing}
              />
            </div>
            <div className={classes.incentivePaymentCell}>
              <SquareButton
                icon="ri-check-line"
                color="green"
                small
                onClick={markAsPayed}
              />
            </div>
          </>
        )}
        <div className={classes.incentivePaymentCell}>
          {incentivePayment.status === IncentivePaymentStatus.PAYED ? (
            <SquareButton
              icon="ri-arrow-go-back-line"
              color="blue"
              small
              onClick={markAsRequested}
            />
          ) : (
            <SquareButton
              icon="ri-delete-bin-line"
              color="red"
              small
              onClick={remove}
            />
          )}
        </div>
      </div>

      {editing && (
        <Formik initialValues={initialValues} onSubmit={onSubmitEdition}>
          <Form>
            <div className={classes.sameLineForm}>
              <Input label="Montant" name="incentive" type="number" small />
              <CurrencyCodeSelect label="Currency" name="currency" small />
              <CountryKeySelect label="Pays" name="countryKey" small />
            </div>

            <SubmitButtonsLine>
              <Button
                variant="transparent"
                small
                color="red"
                text="Annuler"
                type="button"
                onClick={stopEditing}
              />
              <SubmitButton small text="Sauvegarder" />
            </SubmitButtonsLine>
          </Form>
        </Formik>
      )}
    </>
  );
};
